import { Box, Text, VStack } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { memo, useCallback } from "react";
import { useSharedContext } from "../shared-logic/shared-context";
import { FeaturedSection } from "./featured-section/table";
import { BrowseTableContent } from "./tables/browse-table-content";
import { TableRow } from "./utils/tables-metadata";

export type BrowseTableData = {
  tableType: string;
  displayName: string;
  rows: TableRow[];
  isEmptySection: boolean;
};

export type SelectedRowState = {
  row: TableRow;
  tableType: string;
  suspectsContext?: string;
};

type RenderBrowseSectionsParams = {
  browseTableData: BrowseTableData[];
  fhirSdk: FhirBundleSdk;
  handleRowClick: (row: TableRow, tableType: string, suspectsContext?: string) => void;
  onPdfView: (docId: string, docFilename?: string, initialSearchQuery?: string) => void;
  isRowViewed: (tableType: string, rowId: string) => boolean;
  onDocumentDownloaded?: (docId: string) => void;
};

function renderBrowseTableSections({
  browseTableData,
  fhirSdk,
  handleRowClick,
  onPdfView,
  isRowViewed,
  onDocumentDownloaded,
}: RenderBrowseSectionsParams): React.ReactNode[] {
  return browseTableData
    .filter(({ isEmptySection }) => !isEmptySection)
    .map(({ tableType, displayName, rows }) => (
      <Box
        key={tableType}
        id={tableType}
        w="100%"
        mb={2}
        borderRadius="lg"
        bg="bg.surface"
        px={6}
        py={6}
        border="1px"
        borderColor="border"
        boxShadow="sm"
      >
        <BrowseTableContent
          tableType={tableType}
          displayName={displayName}
          rows={rows}
          fhirSdk={fhirSdk}
          handleRowClick={handleRowClick}
          handlePdfView={onPdfView}
          isRowViewed={isRowViewed}
          onDocumentDownloaded={onDocumentDownloaded}
        />
      </Box>
    ));
}

interface BrowseContentProps {
  fhirSdk: FhirBundleSdk;
  children: React.ReactNode;
  browseTableData: BrowseTableData[];
  onFeaturedDataChange?: (hasFeaturedData: boolean) => void;
  onRowSelect: (rowState: SelectedRowState) => void;
  onPdfView: (docId: string, docFilename?: string, initialSearchQuery?: string) => void;
  isRowViewed: (tableType: string, rowId: string) => boolean;
  onDocumentDownloaded?: (docId: string) => void;
}

/**
 * Browse mode content component.
 * Renders tables without search filtering.
 * Lab Panels are handled via toggle in LabTable (not as separate section).
 * Receives pre-computed table data from parent.
 */
function BrowseContentComponent({
  fhirSdk,
  children,
  browseTableData,
  onFeaturedDataChange,
  onRowSelect,
  onPdfView,
  isRowViewed,
  onDocumentDownloaded,
}: BrowseContentProps) {
  const {
    sharedState: { isAmazonUser, isMetriportUser },
  } = useSharedContext();

  /**
   * Handles row click from any table.
   * Notifies parent to handle side panel opening and selection.
   */
  const handleRowClick = useCallback(
    (row: TableRow, tableType: string, suspectsContext?: string) => {
      onRowSelect({ row, tableType, suspectsContext });
    },
    [onRowSelect]
  );

  const allSectionsEmpty = browseTableData.every(table => table.isEmptySection);

  return (
    <Box backgroundColor="bg.canvas">
      <VStack align="stretch">
        <Box id="ai-summary">{children}</Box>
        <VStack align="stretch">
          {(isAmazonUser || isMetriportUser) && (
            <Box id="featured">
              <FeaturedSection
                filteredTableData={browseTableData.map(table => ({
                  ...table,
                  filteredRows: table.rows,
                  index: {} as never,
                  indexingPromises: [],
                }))}
                onRowClick={handleRowClick}
                fhirSdk={fhirSdk}
                onPdfView={onPdfView}
                onFeaturedDataChange={onFeaturedDataChange}
                isRowViewed={isRowViewed}
              />
            </Box>
          )}

          {allSectionsEmpty && (
            <Box
              py={10}
              px={8}
              borderRadius="lg"
              backgroundColor="bg.subtle"
              border="1px"
              borderColor="border"
              textAlign="center"
            >
              <Text fontWeight="semibold" color="fg.muted" fontSize="lg">
                No patient data available
              </Text>
              <Text color="fg.subtle" fontSize="sm" mt={2}>
                This patient does not have any medical records yet.
              </Text>
            </Box>
          )}

          {renderBrowseTableSections({
            browseTableData,
            fhirSdk,
            handleRowClick,
            onPdfView,
            isRowViewed,
            onDocumentDownloaded,
          })}
        </VStack>
      </VStack>
    </Box>
  );
}

export const BrowseContent = memo(BrowseContentComponent);
